import React from "react";
import Moment from "moment";
import { formatDate, isDelayed, isBefore } from "Helpers/date";
import StatusIndicator from "Components/project/Common/StatusIndicator/StatusIndicator";

export const columns = {
    name: {
        column: {name: "name", type: "input"},
        name: "Task Name",
        default: true,
        enabled: true,
        readOnly: true,
        width: 500,
        align: "left",
        sorting: "alphabetical"
    },
    status: {
        column: {name: "status", type: "default"},
        name: "Status",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "alphabetical"
    },
    assignee: {
        column: {name: "assigneeLabel", type: "default", filterKey: 'assigneeId'},
        name: "Assignee",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "alphabetical"
    },
    dueDate: {
        column: {name: "dueDate", type: "date"},
        name: "Due Date",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 130,
        align: "center",
        sorting: "chronological"
    },
    daysRemaining: {
        column: {name: "daysRemaining", type: "date"},
        name: "Days Remaining",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "chronological",
        disableFilters: true
    },
    workstream: {
        column: {name: "workstreamName", type: "default",},
        name: "Workstreams",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 180,
        align: "center",
        sorting: "alphabetical"
    },
    approver: {
        column: {name: "approverName", type: "default", filterKey: 'approverId'},
        name: "Approver",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "alphabetical"
    },
    loggedTime: {
        column: {name: "loggedTime", type: "default"},
        name: "Logged Time",
        default: true,
        enabled: true,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "numerical"
    },
    reviewer: {
        column: {name: "reviewerName", type: "default", filterKey: 'reviewerId'},
        name: "Reviewer",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 130,
        align: "center",
        sorting: "alphabetical"
    },
    followers: {
        column: {name: "followers", type: "customArray", filterKey: 'fullName'},
        name: "Followers",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "alphabetical"
    },
    startDate: {
        column: {name: "startDate", type: "date"},
        name: "Start Date",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "chronological"
    },
    adjustedDate: {
        column: {name: "adjustedDate", type: "date"},
        name: "Adjusted Date",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "chronological"
    },
    applicableFrom: {
        column: {name: "applicableFrom", type: "date"},
        name: "Applicable from",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 180,
        align: "center",
        sorting: "chronological"
    },
    applicableTo: {
        column: {name: "applicableUntil", type: "date"},
        name: "Applicable Until",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 165,
        align: "center",
        sorting: "chronological"
    },
    completionDate: {
        column: {name: "completionDate", type: "date"},
        name: "Completion Date",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 180,
        align: "center",
        sorting: "chronological"
    },
    recurrence: {
        column: {name: "recurrenceLabel", type: "default"},
        name: "Recurrence",
        default: false,
        enabled: false,
        dragDisabled: false,
        width: 150,
        align: "center",
        sorting: "alphabetical"
    }
}

export const EXCLUSIVE_INTERNAL_COLUMNS = [
    'timelog'
];

export const RECURRENCE_LABEL = {
    'months': 'Monthly',
    'years': 'Yearly',
    'weeks': 'Weekly',
    'days': 'Daily',
    'customs': 'Custom',
}

export const categories = [
    {
        id: "today",
        name: "Due Today",
        enabled: false,
        filter: date => Moment(date).isSameOrBefore(Moment().endOf("day")),
        page: 1
    },
    {
        id: "week",
        name: "Due This Week",
        enabled: false,
        filter: date => Moment(date).isAfter(Moment().endOf("day")) && Moment(date).isBefore(Moment().endOf("week")),
        page: 1
    },
    {
        id: "month",
        name: "Due This Month",
        enabled: false,
        filter: date => Moment(date).isAfter(Moment().endOf("week")) && Moment(date).isBefore(Moment().endOf("month")),
        page: 1
    },
    {
        id: "succeedingMonths",
        name: "Succeeding Months",
        enabled: false,
        filter: date => Moment(date).isAfter(Moment().endOf("week")) && Moment(date).isAfter(Moment().endOf("month")),
        page: 1
    }
]

export const COMPONENTS = {
    status: {
        Component: task => (
            <StatusIndicator status={task.status} />
        )
    },
    assignee: {
        Component: task => (
            <div class="taskAssignee">
                <span>{task.assigneeLabel}</span>
            </div>
        )
    },
    dueDate: {
        Component: task => (
            <div class={`taskDueDate ${ isDelayed(task.dueDate) ? "textDue" : ""}`}>
                <span style={{color: isDelayed(task.dueDate) ? "#D34A46" : "inherit"}}>{formatDate(task.dueDate)}</span>
            </div>
        )
    },
    daysRemaining: {
        Component: task => {
            return (
                <div class="taskDaysRemaining">
                    {task.daysRemaining < 0 && <span class="textDue" style={{color: "#D34A46"}}>{(Math.abs(task.daysRemaining))} day/s delayed</span>}
                    {task.daysRemaining === 0 && <span class="textDue" style={{color: "#FBBC05"}}>Today</span>}
                    {task.daysRemaining > 0 && <span>{task.daysRemaining} day/s remaining</span>}
                </div>
            )
        }
    },
    completionDate: {
        Component: task => {
            return (
                <div class={`taskCompletionDate ${ isBefore(task.dueDate, task.completionDate) ? "textDue" : ""}`}>
                    <span style={{color: isBefore(task.dueDate, task.completionDate) ? "#D34A46" : "inherit"}}>{formatDate(task.completionDate)}</span>
                </div>
            )
        }
    },
    workstream: {
        Component: task => (
            <div class="taskWorkstream">
                <span>{task.workstreamName}</span>
            </div>
        )
    },
    loggedTime: {
        Component: task => {
            return (
                <div class="taskTimelog">
                    <span>{task.loggedTime || '00:00'}</span>
                </div>
            )
        }
    },
    approver: {
        Component: task => (
            <div class="taskApprover">
                <span>{task.approverName || 'None'}</span>
            </div>
        )
    },
    reviewer: {
        Component: task => (
            <div class="taskReviewer">
                <span>{task.reviewerName || 'None'}</span>
            </div>
        )
    },
    followers: {
        Component: task => (
            <div class="taskFollowers">
                <span>
                    {task.followers.length
                        ? task.followers.map(follower => follower.fullName).join(', ') 
                        : 'None'
                    }</span>
            </div>
        )
    },
    startDate: {
        Component: task => (
            <div class="taskStartDate">
                <span>{formatDate(task.startDate)}</span>
            </div>
        )
    },
    adjustedDate: {
        Component: task => (
            <div class="taskStartDate">
                <span>{formatDate(task.adjustedDate)}</span>
            </div>
        )
    },
    applicableFrom: {
        Component: task => (
            <div class="taskApplicableFrom">
                <span>{formatDate(task.applicableFrom)}</span>
            </div>
        )
    },
    applicableTo: {
        Component: task => (
            <div class="taskApplicableTo">
                <span>{formatDate(task.applicableUntil)}</span>
            </div>
        )
    },
    recurrence: {
        Component: task => (
            <div class="taskRecurrence">
                <span>{task.recurrenceLabel}</span>
            </div>
        )
    }
}